'use client';

// useCreateTempBot - TanStack Query mutation for creating temporary bots
// Story 6.5: Added referrerBotId for viral attribution

import { useMutation } from '@tanstack/react-query';
import { saveSessionToStorage, getOrCreateClientSession } from '../lib/session';
import { getReferral, clearReferral } from '../lib/referralTracking';

interface CreateTempBotRequest {
  content: string;
  /** Optional bot ID to add content to an existing bot instead of creating new */
  botId?: string;
  /** Optional referrer bot ID for viral tracking attribution (Story 6.5) */
  referrerBotId?: string;
}

interface CreateTempBotResponse {
  data: {
    botId: string;
    status: 'draft';
  };
}

interface CreateTempBotError {
  error: {
    code: string;
    message: string;
    details?: Record<string, string[]>;
  };
}

/**
 * Ensures session is synced between server cookie and localStorage BEFORE bot creation.
 * This prevents mismatch where client has one session but server creates another.
 */
async function ensureSessionSync(): Promise<void> {
  try {
    // First, check if we have a localStorage session
    const localSession = getOrCreateClientSession();

    // Sync with server - POST the local session to set the cookie
    // This ensures server and client use the SAME session ID
    const response = await fetch('/api/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: localSession }),
    });

    const data = await response.json();
    if (data.data?.sessionId) {
      // Update localStorage with server-confirmed session
      saveSessionToStorage(data.data.sessionId);
    }
  } catch {
    // If sync fails, the server will create its own session
    // Bot will still be created, just with potentially different session
    console.warn('[session] Pre-request session sync failed');
  }
}

/**
 * Creates a temporary bot via the API, or adds content to an existing bot
 */
async function createTempBot(
  request: CreateTempBotRequest
): Promise<CreateTempBotResponse> {
  // Sync session BEFORE creating bot to ensure consistency
  await ensureSessionSync();

  // Get referrer bot ID from request or from stored referral (Story 6.5)
  const storedReferral = getReferral();
  const referrerBotId = request.referrerBotId || storedReferral || undefined;

  // Debug logging for Story 6.5
  if (process.env.NODE_ENV === 'development') {
    console.log('[useCreateTempBot] Referral debug:', {
      storedReferral,
      requestReferrerBotId: request.referrerBotId,
      finalReferrerBotId: referrerBotId,
      requestBotId: request.botId,
      isNewBot: !request.botId,
    });
  }

  // If botId is provided, add content to existing bot
  // Otherwise, create a new temporary bot
  const endpoint = request.botId
    ? `/api/bots/${request.botId}/content`
    : '/api/bots/temp';

  // Build request body - include referrerBotId only for new bot creation
  const body = request.botId
    ? { content: request.content }
    : { content: request.content, referrerBotId };

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok) {
    throw data as CreateTempBotError;
  }

  // If adding to existing bot, normalize response to match expected format
  if (request.botId) {
    return {
      data: {
        botId: request.botId,
        status: 'draft',
      },
    };
  }

  // Clear referral after successful bot creation (Story 6.5)
  // The referral has been attributed, don't use it again
  if (referrerBotId) {
    clearReferral();
  }

  return data as CreateTempBotResponse;
}

export interface UseCreateTempBotOptions {
  onSuccess?: (data: CreateTempBotResponse) => void;
  onError?: (error: CreateTempBotError) => void;
}

/**
 * Hook for creating a temporary bot
 *
 * Uses TanStack Query's useMutation for proper loading/error state management.
 *
 * @example
 * ```tsx
 * const { mutate, isPending, isError, error } = useCreateTempBot({
 *   onSuccess: (data) => router.push(`/bot/${data.data.botId}/test`),
 * });
 *
 * // To create a bot
 * mutate({ content: 'My bot content...' });
 *
 * // Access loading state
 * if (isPending) return <ProcessingIndicator />;
 * ```
 */
export function useCreateTempBot(options?: UseCreateTempBotOptions) {
  return useMutation({
    mutationFn: createTempBot,
    onSuccess: (data) => {
      // Session was synced before creation, no need to sync again
      options?.onSuccess?.(data);
    },
    onError: (error: CreateTempBotError) => {
      options?.onError?.(error);
    },
  });
}

// Export types
export type {
  CreateTempBotRequest,
  CreateTempBotResponse,
  CreateTempBotError,
};
